"use client"

import * as React from "react"
import { useRef, useEffect, useMemo, useState, useCallback } from "react"
import { gsap } from "gsap"
import { Draggable } from "gsap/Draggable"
import { motion } from "framer-motion"
import { useTranslations } from "@/components/I18nProvider"
import { Button } from "@sarkiai/ui"
import { ChevronLeft, ChevronRight } from "lucide-react"
import Link from "next/link"
import TrueFocus from "@/components/TrueFocus"

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(Draggable)
}

interface VCard {
  id: string
  title: string
  description: string
  backgroundImage: string
  borderColor: string // Dominant color for the border
}

const HERO_IMAGES_PATH = "/assets/images/hero/"
const CORPORATE_IMAGES_PATH = "/assets/images/hero-corporate/"

export default function HeroCarousel() {
  const t = useTranslations("hero")
  const cardsRef = useRef<HTMLUListElement>(null)
  const dragProxyRef = useRef<HTMLDivElement>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isReady, setIsReady] = useState(false)
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null)

  // Refs for GSAP animations
  const seamlessLoopRef = useRef<gsap.core.Timeline | null>(null)
  const scrubRef = useRef<gsap.core.Tween | null>(null)
  const playheadRef = useRef({ offset: 0 })

  const spacing = 0.1
  const autoPlayInterval = 4000 // 4 seconds

  // VCard data with new hero images and SarkiAI themed content
  const vcards = useMemo<VCard[]>(() => [
    {
      id: "hero-1",
      title: t("carousel.africa.title"),
      description: t("carousel.africa.description"),
      // backgroundImage: `${HERO_IMAGES_PATH}hero-1.webp`,
      backgroundImage: `${CORPORATE_IMAGES_PATH}ceo-djotta-portrait-2-bodyguard.webp`,
      // borderColor: "#f1c40f", // Gold/Yellow - African pattern
      // borderColor: "#00f2ff", // Electric Cyan
      borderColor: "#1c8fad",
    },
    {
      id: "hero-3",
      title: t("carousel.innovation.title"),
      description: t("carousel.innovation.description"),
      // backgroundImage: `${HERO_IMAGES_PATH}hero-3.webp`,
      backgroundImage: `${CORPORATE_IMAGES_PATH}ceo-djotta-portrait-1.webp`,
      // borderColor: "#8b5cf6", // Purple - Innovation
      // borderColor: "#00a8ff", // Deep Sky Blue
      borderColor: "#1c8fad",
    },
    {
      id: "hero-4",
      title: t("carousel.enterprise.title"),
      description: t("carousel.enterprise.description"),
      // backgroundImage: `${HERO_IMAGES_PATH}hero-4.webp`,
      backgroundImage: `${CORPORATE_IMAGES_PATH}ceo-djotta-portrait.webp`,
      // borderColor: "#3b82f6", // Blue - Enterprise
      // borderColor: "#4834d4", // Indigo Blue
      borderColor: "#1c8fad",
    },
    {
      id: "hero-6",
      title: t("carousel.automation.title"),
      description: t("carousel.automation.description"),
      // backgroundImage: `${HERO_IMAGES_PATH}hero-6.webp`,
      backgroundImage: `${CORPORATE_IMAGES_PATH}ceo-djotta-portrait-3.webp`,
      // borderColor: "#a855f7", // Purple - Automation
      // borderColor: "#10ac84", // Teal/Emerald
      borderColor: "#1c8fad",
    }
  ], [t])

  // Double the cards for seamless looping
  const displayCards = useMemo(() => [...vcards, ...vcards, ...vcards], [vcards])

  // Navigation function stored in ref for access from handlers
  const scrollToOffsetRef = useRef<((offset: number) => void) | null>(null)

  // Auto-play function
  const startAutoPlay = useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current)
    }
    autoPlayRef.current = setInterval(() => {
      if (scrubRef.current && scrollToOffsetRef.current) {
        scrollToOffsetRef.current(scrubRef.current.vars.offset + spacing)
      }
    }, autoPlayInterval)
  }, [spacing])

  const stopAutoPlay = useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current)
      autoPlayRef.current = null
    }
  }, [])

  useEffect(() => {
    if (typeof window === "undefined" || !cardsRef.current) return

    const cards = gsap.utils.toArray<HTMLLIElement>(".hero-card")
    if (cards.length === 0) return

    // Set initial state
    gsap.set(cards, { xPercent: 400, opacity: 0, scale: 0 })

    const snapTime = gsap.utils.snap(spacing)

    // Animation function for each card
    const animateFunc = (element: HTMLLIElement) => {
      const tl = gsap.timeline()
      tl.fromTo(
        element,
        { scale: 0, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          zIndex: 100,
          duration: 0.5,
          yoyo: true,
          repeat: 1,
          ease: "power1.in",
          immediateRender: false,
        }
      ).fromTo(
        element,
        { xPercent: 400 },
        { xPercent: -400, duration: 1, ease: "none", immediateRender: false },
        0
      )
      return tl
    }

    // Build seamless loop
    const buildSeamlessLoop = (
      items: HTMLLIElement[],
      spacing: number,
      animateFunc: (element: HTMLLIElement) => gsap.core.Timeline
    ) => {
      const overlap = Math.ceil(1 / spacing)
      const startTime = items.length * spacing + 0.5
      const loopTime = (items.length + overlap) * spacing + 1
      const rawSequence = gsap.timeline({ paused: true })
      const seamlessLoop = gsap.timeline({
        paused: true,
        repeat: -1,
        onRepeat() {
          if (this._time === this._dur) {
            this._tTime += this._dur - 0.01
          }
        },
      })
      const l = items.length + overlap * 2

      for (let i = 0; i < l; i++) {
        const index = i % items.length
        const time = i * spacing
        rawSequence.add(animateFunc(items[index]), time)
        if (i <= items.length) {
          seamlessLoop.add("label" + i, time)
        }
      }

      rawSequence.time(startTime)
      seamlessLoop
        .to(rawSequence, {
          time: loopTime,
          duration: loopTime - startTime,
          ease: "none",
        })
        .fromTo(
          rawSequence,
          { time: overlap * spacing + 1 },
          {
            time: startTime,
            duration: startTime - (overlap * spacing + 1),
            immediateRender: false,
            ease: "none",
          }
        )
      return seamlessLoop
    }

    const seamlessLoop = buildSeamlessLoop(cards, spacing, animateFunc)
    seamlessLoopRef.current = seamlessLoop

    const wrapTime = gsap.utils.wrap(0, seamlessLoop.duration())

    const scrub = gsap.to(playheadRef.current, {
      offset: 0,
      onUpdate() {
        seamlessLoop.time(wrapTime(playheadRef.current.offset))
        // Update current index based on offset
        const normalizedOffset = Math.abs(playheadRef.current.offset % vcards.length)
        const newIndex = Math.round(normalizedOffset / spacing) % vcards.length
        if (newIndex >= 0 && newIndex < vcards.length) {
          setCurrentIndex(newIndex)
        }
      },
      duration: 0.5,
      ease: "power3",
      paused: true,
    })
    scrubRef.current = scrub

    // Navigation function (button-based only, no scroll)
    const scrollToOffset = (offset: number) => {
      const snappedTime = snapTime(offset)
      scrub.vars.offset = snappedTime
      scrub.invalidate().restart()
    }

    scrollToOffsetRef.current = scrollToOffset

    // Draggable (optional - for touch/mouse drag)
    if (dragProxyRef.current) {
      Draggable.create(dragProxyRef.current, {
        type: "x",
        trigger: cardsRef.current,
        onPress() {
          this.startOffset = scrub.vars.offset
          stopAutoPlay()
        },
        onDrag() {
          scrub.vars.offset = this.startOffset + (this.startX - this.x) * 0.001
          scrub.invalidate().restart()
        },
        onDragEnd() {
          scrollToOffset(scrub.vars.offset)
          startAutoPlay()
        },
      })
    }

    // Initialize first card visible
    scrub.vars.offset = spacing
    scrub.invalidate().restart()

    // Start auto-play
    startAutoPlay()

    // Mark carousel as ready
    setIsReady(true)

    return () => {
      seamlessLoop.kill()
      scrub.kill()
      stopAutoPlay()
    }
  }, [vcards.length, spacing, startAutoPlay, stopAutoPlay])

  const handlePrev = useCallback(() => {
    if (scrubRef.current && scrollToOffsetRef.current) {
      scrollToOffsetRef.current(scrubRef.current.vars.offset - spacing)
      // Reset auto-play timer
      startAutoPlay()
    }
  }, [spacing, startAutoPlay])

  const handleNext = useCallback(() => {
    if (scrubRef.current && scrollToOffsetRef.current) {
      scrollToOffsetRef.current(scrubRef.current.vars.offset + spacing)
      // Reset auto-play timer
      startAutoPlay()
    }
  }, [spacing, startAutoPlay])

  return (
    <section id="hero-section" className="relative w-full flex flex-col">
      {/* Global Headline */}
      <div className="w-full max-w-4xl mx-auto px-4 pt-16 md:pt-14 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl md:text-2xl uppercase tracking-tighter leading-tight"
        >
          <TrueFocus
            sentence={t('headline_separator')}
            separator="|"
            manualMode={false}
            blurAmount={5}
            borderColor="hsl(var(--primary))"
            animationDuration={.1}
            pauseBetweenAnimations={4}
          />
        </motion.h1>
      </div>

      {/* Gallery - transparent background */}
      <div className={`relative w-full h-[40vh] md:h-[55vh] overflow-hidden transition-opacity duration-300 ${isReady ? 'opacity-100' : 'opacity-0'}`}>
        {/* Cards Container */}
        <ul
          ref={cardsRef}
          className="hero-cards absolute w-44 h-64 md:w-56 md:h-80 top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          {displayCards.map((vcard, index) => (
            <li
              key={`${vcard.id}-${index}`}
              className="hero-card list-none p-0 m-0 text-center absolute top-0 left-0"
              style={{
                width: "clamp(11rem, 30vw, 15rem)",
                height: "clamp(17rem, 45vw, 24.89rem)",
              }}
            >
              <div
                className="w-full h-full rounded-3xl bg-cover bg-no-repeat bg-center shadow-2xl"
                style={{
                  backgroundImage: `url(${vcard.backgroundImage})`,
                  border: `2px solid ${vcard.borderColor}`,
                  boxShadow: `0 0 20px color-mix(in srgb, ${vcard.borderColor}, transparent 75%), 0 25px 50px -12px rgba(0, 0, 0, 0.25)`,
                }}
              />
            </li>
          ))}
        </ul>
      </div>

      {/* Content Section - below cards with navigation */}
      <div className="relative w-full max-w-2xl mx-auto px-4 py-6">
        {/* Navigation Arrows - positioned around content */}
        <Button
          variant="outline"
          size="icon"
          onClick={handlePrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full border-foreground/30 text-foreground dark:text-white"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full border-foreground/30 text-foreground dark:text-white"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>

        <div className="flex flex-col items-center gap-4 px-12">
          <h2 className="text-2xl md:text-3xl uppercase tracking-tighter leading-none text-center text-foreground">
            {vcards[currentIndex]?.title || vcards[0].title}
          </h2>
          <p className="text-sm text-center text-foreground max-w-sm">
            {vcards[currentIndex]?.description || vcards[0].description}
          </p>
          <Button asChild size="lg" className="rounded-full px-8 bg-black uppercase tracking-widest text-xs">
            <Link href="/contact">
              {t("order")}
            </Link>
          </Button>
        </div>
      </div>

      {/* Drag Proxy (invisible) */}
      <div ref={dragProxyRef} className="drag-proxy invisible absolute" />
    </section>
  )
}
